import { p as proxyCustomElement, H, c as createEvent, h } from './index.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { d as defineCustomElement$8, C as CopyToClipboardButton } from './p-BTgD4Fvz.js';
import { d as defineCustomElement$a } from './p-Cd2X-6KG.js';
import { d as defineCustomElement$9 } from './p-BedvZv52.js';
import { d as defineCustomElement$7 } from './p-DZKBv4tL.js';
import { d as defineCustomElement$6 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$5 } from './p-BVCTlSQE.js';
import { d as defineCustomElement$4 } from './p-DaiIyqo3.js';
import { d as defineCustomElement$3 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$2 } from './p-QtR5VUMp.js';

const getLanguageLabel = (language) => {
    if (language === 'js') {
        return 'JavaScript';
    }
    if (language === 'ts') {
        return 'TypeScript';
    }
    if (language === 'plaintext') {
        return 'Plaintext';
    }
    return language.toUpperCase();
};

const codeEditorCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.25rem;box-sizing:border-box;display:block;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;transition:background-color .2s ease,border-color .2s ease;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host(:hover:not([disabled])){border:1px solid var(--jw-theme-form-border-regular-hover-border-color)}:host(:focus:not([disabled])){border:1px solid var(--jw-theme-form-border-regular-focus-border-color);outline:var(--jw-theme-effects-focus-default-outline)}wui-code-snippet{border:none}wui-code-snippet::part(content){padding:0}.header{align-items:center;background-color:var(--jw-theme-form-addon-default-bg-color);border-top-left-radius:.25rem;border-top-right-radius:.25rem;color:var(--jw-theme-form-addon-default-color);display:flex;font-family:Open Sans;font-size:.875rem;font-weight:600;gap:.75rem;justify-content:space-between;letter-spacing:-.0004em;line-height:1.25rem}.header .language{padding:0 1rem}.error-message{align-items:center;align-self:stretch;background-color:var(--jw-theme-warning-bold-message-bg-color);color:var(--jw-theme-warning-bold-message-color);display:flex;font-family:Open Sans;font-size:.875rem;font-weight:600;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;padding:0 1rem}.main{background-color:var(--jw-theme-form-field-default-bg-color);border-bottom-left-radius:.25rem;border-bottom-right-radius:.25rem;display:flex;gap:.75rem;padding:.5rem 1rem}:host([error-type]) .header{border-bottom:1px solid var(--jw-theme-warning-muted-message-border-color)}:host([loading]){pointer-events:none}:host([disabled]){border-color:var(--jw-theme-form-border-regular-default-border-color)}:host([disabled]) .header{background:var(--jw-theme-form-field-disabled-bg-color);color:var(--jw-theme-form-field-disabled-color)}:host([disabled]) wui-code-snippet::part(content){cursor:not-allowed;opacity:.4}";

const WuiCodeEditor$1 = /*@__PURE__*/ proxyCustomElement(class WuiCodeEditor extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validateInternal = () => {
            if (!this.value) {
                return;
            }
            if (this.language === 'json') {
                try {
                    JSON.parse(this.value);
                }
                catch {
                    return {
                        validationMessage: 'Invalid or empty JSON',
                        validity: getValidityObject({ customError: true, valueMissing: false, valid: false }),
                    };
                }
            }
            // Return valid state
            return {
                validationMessage: '',
                validity: getValidityObject(),
            };
        };
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        /**
         * Syntax highlighting language.
         *
         * Recommended to set this manually instead of doing automatic assertion.
         */
        this.language = 'plaintext';
        /**
         * Prepend extra buttons to the header
         */
        this.buttons = ['copy-to-clipboard'];
        this.handleInput = (evt) => {
            const elm = evt.target;
            const value = elm.value;
            // Stop the inner input event from bubbling up
            evt.stopPropagation();
            this.dirty = true;
            this.checkValidity();
            this.updateState({ value });
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleKeyDown = (evt) => {
            const elm = evt.currentTarget;
            switch (evt.key) {
                case 'Tab': {
                    evt.preventDefault();
                    const start = elm.selectionStart;
                    const end = elm.selectionEnd;
                    // Insert tab in string between start and end
                    const value = `${elm.value.substring(0, start)}\t${elm.value.substring(end)}`;
                    const selectionEnd = start + 1;
                    const selectionStart = selectionEnd;
                    this.updateState({ value, selectionStart, selectionEnd });
                }
            }
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    updateState({ value, selectionStart, selectionEnd, }) {
        this.selectionStart = selectionStart || this.inputElm?.selectionStart;
        this.selectionEnd = selectionEnd || this.inputElm?.selectionEnd;
        this.value = value ?? '';
    }
    componentDidRender() {
        if (this.inputElm) {
            // Always ensure textarea has correct props on each render
            if (typeof this.selectionStart !== 'undefined') {
                this.inputElm.selectionStart = this.selectionStart;
            }
            if (typeof this.selectionEnd !== 'undefined') {
                this.inputElm.selectionEnd = this.selectionEnd;
            }
        }
    }
    renderInner() {
        const errorMessage = this.getError().message;
        return (
        // Use div instead of fragment, to prevent the form-field gap from appearing
        h("div", null, h("div", { class: "header" }, h("div", { class: "language" }, this.language ? getLanguageLabel(this.language) : '', " Code Editor"), this.buttons?.length && (h("wui-layout", { part: "wui-layout", type: "button-group", variant: "button-group-tight" }, this.buttons.map((button) => {
            if (button === 'copy-to-clipboard') {
                return h(CopyToClipboardButton, { code: this.value });
            }
            return (h("wui-button", { key: button.title, onClick: () => button.action(this.elm), title: button.title, type: "icon", icon: button.icon, disabled: this.disabled }));
        })))), h("div", { class: "main" }, h("wui-code-snippet", { code: 
            // there's something odd with hljs probably... forcing me to add an extra newline
            this.value ? `${this.value}\n` : '', language: this.language, loading: this.loading, type: "multiline", buttons: [] }, h("textarea", { slot: "textarea", ref: this.setInputRef, spellcheck: false, value: this.value ?? '', name: this.name, required: !!this.required, disabled: this.disabled, onInput: this.handleInput, onKeyDown: this.handleKeyDown, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            } }))), errorMessage && h("div", { class: "error-message" }, errorMessage)));
    }
    render() {
        return renderFormField(this, this.renderInner());
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return codeEditorCss; }
}, [257, "wui-code-editor", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "truncate": [516],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "language": [1],
        "buttons": [16],
        "name": [1],
        "disabled": [516],
        "loading": [516],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-code-editor", "wui-box", "wui-button", "wui-code-snippet", "wui-form-field", "wui-icon", "wui-layout", "wui-loader", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-code-editor":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiCodeEditor$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-code-snippet":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiCodeEditor = WuiCodeEditor$1;
const defineCustomElement = defineCustomElement$1;

export { WuiCodeEditor, defineCustomElement };
